import { useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';

import InputAdornmentFiled from '@design-system/InputAdornmentFiled';

import ButtonPlus from '@experimental-components/ButtonPlus';
import IconSearch from '@experimental-components/IconsComponents/SearchOutline';

import { TypeOfSaleFilterValues } from 'components/ProductForm/types';

import SelectCategories from './SelectCategories';
import TypeSoldProducts from './TypeSoldProducts';
import SelectBrands from './SelectBrands';
import SortByProducts from './SortByProducts';
import useStyles from './styles';

function HeaderProductsFilters({
  categoriesList,
  isOrganizationMode,
  onAddButtonClick,
  onSearchBoxChange,
  onSelectedCategoriesChange,
  onSelectTypeSoldProductChange,
  onSelectBrandsChange,
  onSelectedOrderBy,
  openDrawer,
  ...others
}) {
  const classes = useStyles({ openDrawer });
  const { t } = useTranslation('menuMaker');

  const [, setFiltersLocals] = useState({
    filterByCategories: [],
    filterByBrands: [],
    page: 1,
    filterByText: null,
    orderBy: [],
  });

  function handleChange(event) {
    const { value } = event.target;

    if (onSearchBoxChange) {
      if (value) {
        setFiltersLocals((currentFilters) => {
          onSearchBoxChange({ ...currentFilters, filterByText: value });
          return { ...currentFilters, filterByText: value };
        });
      } else {
        setFiltersLocals((currentFilters) => {
          onSearchBoxChange({ ...currentFilters, filterByText: null });
          return { ...currentFilters, filterByText: null };
        });
      }
    }
  }

  function handleSelectedCategoriesChange(value, withoutCategories) {
    setFiltersLocals((currentFilters) => {
      onSelectedCategoriesChange({ ...currentFilters, filterByCategories: value, withoutCategories });
      return { ...currentFilters, filterByCategories: value, withoutCategories };
    });
  }

  function handleFilterByTypeSoldProductChange(value) {
    setFiltersLocals((currentFilters) => {
      onSelectTypeSoldProductChange({
        ...currentFilters,
        typeOfSale: value === TypeOfSaleFilterValues.ALL ? null : value,
      });
      return { ...currentFilters, typeOfSale: value === TypeOfSaleFilterValues.ALL ? null : value };
    });
  }

  function handleFilterByBrands(value, withoutBrands) {
    setFiltersLocals((currentFilters) => {
      onSelectBrandsChange({ ...currentFilters, filterByBrands: value, withoutBrands });
      return { ...currentFilters, filterByBrands: value, withoutBrands };
    });
  }

  function handleSortBy(value) {
    setFiltersLocals((currentFilters) => {
      onSelectedOrderBy({ ...currentFilters, orderBy: [value] });
      return { ...currentFilters, orderBy: [value] };
    });
  }

  return (
    <Box className={classes.root} {...others}>
      <Box className={classes.searchProducts}>
        <InputAdornmentFiled
          data-testid="searchBoxProducts"
          fullWidth
          hideLabelHelperText
          id="searchBoxProducts"
          onChange={debounce(handleChange, 300)}
          placeholder={t('menuMaker:headerForm.searchProduct')}
          startAdornment={
            <InputAdornment position="start">
              <IconSearch />
            </InputAdornment>
          }
        />
      </Box>

      <Box className={classes.wrapperMultiSelect}>
        <SelectBrands onSelectedBrands={handleFilterByBrands} />
      </Box>

      <Box className={classes.wrapperMultiSelect}>
        <SelectCategories categories={categoriesList} onSelectedCategories={handleSelectedCategoriesChange} />
      </Box>

      <Box className={classes.wrapperMultiSelect}>
        <TypeSoldProducts onSelectTypeSoldProduct={handleFilterByTypeSoldProductChange} />
      </Box>

      <Box className={classes.wrapperActions}>
        <SortByProducts data-testid="orderByProducts" id="orderByProducts" onSelectedSortBy={handleSortBy} />
        {isOrganizationMode && (
          <ButtonPlus className={classes.middlePrimaryButton} data-testid="btnAddNewProduct" onClick={onAddButtonClick}>
            {!openDrawer ? t('menuMaker:buttons.product') : ''}
          </ButtonPlus>
        )}
      </Box>
    </Box>
  );
}

HeaderProductsFilters.propTypes = {
  categoriesList: PropTypes.object,
  isOrganizationMode: PropTypes.bool,
  onAddButtonClick: PropTypes.func,
  onSearchBoxChange: PropTypes.func,
  onSelectedCategoriesChange: PropTypes.func,
  onSelectTypeSoldProductChange: PropTypes.func,
  onSelectBrandsChange: PropTypes.func,
  onSelectedOrderBy: PropTypes.func,
  openDrawer: PropTypes.bool,
};

export default HeaderProductsFilters;
